import axios from 'axios';
import { harnessClient, HARNESS_BASE_URL } from './client';
import { clearAuthState, broadcastLogoutEvent } from '../utils/authHelpers';
import { getAccessToken } from '../auth/accessToken';
import { forgetThisDevice } from '../push/subscribe';
import type { OrganisationKind } from '../../types/Organisation';

/**
 * Authentication API functions
 * Epic 2, Story 2.1: Password Authentication implementation
 */

export interface User {
  id: string;
  tenant_id: string; // Added in Story 2.2: User-tenant association
  email: string;
  name: string;
  role: string;
  avatar_url?: string;
  is_superuser?: boolean; // Added in Story PPA-2.1: Superuser role for prompt management
}

export interface Tenant {
  id: string;
  name: string;
}

/**
 * Story 17.2 — no `refresh_token`: the harness sets it as the HttpOnly
 * `amatis_refresh` cookie on login / register / register-invited / refresh and
 * never puts it in a body. Those calls send `withCredentials: true` so the
 * browser stores the cookie (dev is cross-origin :5173 → :8080).
 */
export interface LoginResponse {
  access_token: string;
  expires_at: string;
  user: User;
  tenant: Tenant;
}

/**
 * Story 13.1 — a signup response: the five login fields plus the harness's
 * `redirect_to` landing path (present on register and register-invited, never
 * on login). Pass it through `safeRedirectPath` before navigating.
 */
export interface SignupResponse extends LoginResponse {
  redirect_to?: string;
}

export interface RegisterRequest {
  email: string;
  password: string;
  name: string;
  kind: OrganisationKind;
  description?: string;
}

export interface LoginRequest {
  email: string;
  password: string;
}

/**
 * Pick the five LoginResponse fields from an auth response body, dropping any
 * extra keys the endpoint may add.
 */
const pickLoginResponse = (response: LoginResponse): LoginResponse => ({
  access_token: response.access_token,
  expires_at: response.expires_at,
  user: response.user,
  tenant: response.tenant,
});

/**
 * Story 13.1 — the five login fields plus `redirect_to` when the body carries
 * a string one (register / register-invited). Any other extra key is dropped.
 */
export const pickSignupResponse = (response: SignupResponse): SignupResponse => ({
  ...pickLoginResponse(response),
  ...(typeof response.redirect_to === 'string' ? { redirect_to: response.redirect_to } : {}),
});

/**
 * Register a new user with email and password
 * @param data - Registration data (email, password, name)
 * @returns Authentication tokens and user data
 */
export const register = async (data: RegisterRequest): Promise<SignupResponse> => {
  // Registration is served in-process by the agent harness (audit A1 chunk 7).
  // harnessClient does NOT unwrap to `.data`, so read it here (mirrors login).
  // The harness returns user/tenant as nested objects.
  const res = await harnessClient.post('/api/auth/register', data, { withCredentials: true });
  return pickSignupResponse(res.data as SignupResponse);
};

/**
 * Login with email and password
 * @param data - Login credentials (email, password)
 * @returns Authentication tokens and user data
 */
export const login = async (data: LoginRequest): Promise<LoginResponse> => {
  // Login is served in-process by the agent harness (audit A1 chunk 5).
  // harnessClient does NOT unwrap to `.data`, so read it here. The harness
  // returns user/tenant as nested objects.
  const res = await harnessClient.post('/api/auth/login', data, { withCredentials: true });
  return pickLoginResponse(res.data as LoginResponse);
};

/**
 * Request a password reset email (T-20260913-1c).
 * The harness answers the SAME 200 whether or not the account exists, so this
 * resolves with nothing. Errors (400 validation, 429, network) propagate as the
 * axios error.
 */
export const requestPasswordReset = async (email: string): Promise<void> => {
  await harnessClient.post('/api/auth/forgot-password', { email });
};

/** A reset link's state (story 17.4) — drives the reset page's landing copy. */
export type ResetTokenState = 'valid' | 'expired' | 'used' | 'invalid';

const RESET_FAILURE_STATES: ReadonlySet<string> = new Set(['expired', 'used', 'invalid']);

/**
 * Normalise a harness-reported failure state. Anything unrecognised (or a
 * pre-17.4 harness that sends no state) is `invalid`.
 */
export function toResetFailureState(value: unknown): Exclude<ResetTokenState, 'valid'> {
  return typeof value === 'string' && RESET_FAILURE_STATES.has(value)
    ? (value as Exclude<ResetTokenState, 'valid'>)
    : 'invalid';
}

/**
 * Pre-check a reset token without consuming it (T-20260913-1c).
 * POSTs the token in the JSON body — never a query param, which would land in
 * access-log request lines (T-20260914-1).
 * @returns `'valid'` only when the harness answers `{ valid: true }`; otherwise
 * the reported `state` (`expired | used | invalid`, story 17.4), defaulting to
 * `'invalid'`.
 */
export const validateResetToken = async (token: string): Promise<ResetTokenState> => {
  const res = await harnessClient.post('/api/auth/validate-reset-token', { token });
  const data = res.data as { valid?: unknown; state?: unknown } | undefined;
  if (data?.valid === true) return 'valid';
  return toResetFailureState(data?.state);
};

/**
 * Set a new password with a reset token (T-20260913-1c). Does NOT sign in.
 * A 400 `invalid_or_expired_token` (with `reason`, story 17.4) /
 * `validation_error` or 429 propagates as the axios error.
 */
export const resetPassword = async (token: string, password: string): Promise<void> => {
  await harnessClient.post('/api/auth/reset-password', { token, password });
};

/**
 * Logout and invalidate session
 * Calls backend to blacklist tokens and clears local state
 * Story 2.6: Server-side token blacklist implementation
 *
 * Story 17.2: the harness route is public — the HttpOnly refresh cookie
 * (`withCredentials: true`) identifies the session to revoke, and the memory
 * bearer, when present, is sent only so its jti can be blacklisted. So the call
 * is always made, even with no access token (an expired / reloaded tab can
 * still revoke its cookie). A pre-17.2 tab that still has a legacy
 * `refresh_token` key sends it in the body; the harness uses it only when no
 * cookie arrives.
 *
 * Story 30.3 (ruling O14) — before the logout POST, `forgetThisDevice` deletes
 * this browser's push subscription row (best effort, bare axios) and always
 * unsubscribes, so the next person on a shared device gets nothing meant for
 * this one. Capped at 1.5s and never throws, so it can never block sign-out.
 */
export const logout = async (): Promise<void> => {
  try {
    await forgetThisDevice(getAccessToken() ?? null);
  } catch (error) {
    // Never blocks the revocation POST below.
    console.error('Logout push cleanup error:', error);
  }
  try {
    const accessToken = getAccessToken();
    let legacyRefreshToken: string | null = null;
    try {
      legacyRefreshToken = localStorage.getItem('refresh_token');
    } catch {
      legacyRefreshToken = null;
    }

    // BARE axios — NOT harnessClient — because harnessClient carries the shared
    // 401→refresh interceptor: a refresh triggered by logout would mint a NEW
    // refresh cookie right as we try to revoke the old one.
    const logoutUrl = `${HARNESS_BASE_URL}/api/auth/logout`;
    await axios.post(
      logoutUrl,
      legacyRefreshToken ? { refresh_token: legacyRefreshToken } : {},
      {
        withCredentials: true,
        headers: accessToken ? { Authorization: `Bearer ${accessToken}` } : {},
      },
    );
  } catch (error) {
    console.error('Logout error:', error);
    // Clear state anyway (force logout even if API fails)
  } finally {
    clearAuthState();

    // Broadcast logout to other tabs
    broadcastLogoutEvent();

    // Redirect to login
    window.location.href = '/login';
  }
};
